import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { usePermission } from "../hooks/usePermission";
import { apiErrorMessage } from "../utils/timesheets";
import { orderRolesAsTree } from "../utils/roleHierarchy";

const COLUMNS = [
  { key: "name", label: "Naam", type: "text" },
  { key: "category", label: "Categorie", type: "text" },
  { key: "description", label: "Beschrijving", type: "text" },
  { key: "user_count", label: "Gebruikers", type: "number" },
  { key: "permission_count", label: "Rechten", type: "number" },
  { key: "created_at", label: "Aangemaakt", type: "date" },
];

const isEmpty = (value) => value === null || value === undefined || value === "";

function compare(a, b, column) {
  const x = a[column.key];
  const y = b[column.key];
  if (column.type === "number") return Number(x) - Number(y);
  if (column.type === "date") return new Date(x) - new Date(y);
  return String(x).localeCompare(String(y), "nl", { sensitivity: "base" });
}

/**
 * Overzicht van de rollen: standaard als hiërarchie (primaire rollen met hun onderliggende rollen eronder);
 * een klik op een kolomkop sorteert plat. Filter op naam, categorie en beschrijving. Een rij opent de rolpagina.
 */
export default function RoleList() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("roles.manage");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" }); // key null = hiërarchie
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", description: "", is_primary: false, parent_role_id: "" });
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    apiFetch("/roles")
      .then(setRoles)
      .catch((err) => setMessage({ text: apiErrorMessage(err, "Fout bij ophalen rollen"), type: "error" }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3500);
    return () => clearTimeout(timer);
  }, [message]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const column = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[0];
    const nameColumn = COLUMNS[0];
    const filtered = roles.filter((r) => !q || `${r.name} ${r.category || ""} ${r.description || ""}`.toLowerCase().includes(q));
    if (!sort.key) return orderRolesAsTree(filtered);
    return filtered
      .map((r) => ({ ...r, depth: 0 }))
      .sort((a, b) => {
        const emptyA = isEmpty(a[column.key]);
        const emptyB = isEmpty(b[column.key]);
        // lege waarden staan altijd onderaan, ook bij aflopend sorteren
        if (emptyA || emptyB) return emptyA === emptyB ? compare(a, b, nameColumn) : emptyA ? 1 : -1;
        return (sort.dir === "asc" ? 1 : -1) * compare(a, b, column) || compare(a, b, nameColumn);
      });
  }, [roles, filter, sort]);

  const toggleSort = (key) => setSort((cur) => (cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const create = async (e) => {
    e.preventDefault();
    try {
      const role = await apiFetch("/roles", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          category: form.category.trim() || null,
          description: form.description.trim() || null,
          is_primary: form.is_primary,
          parent_role_id: form.is_primary || !form.parent_role_id ? null : Number(form.parent_role_id),
        }),
      });
      navigate(`/roles/${role.id}`);
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, "Fout bij aanmaken rol"), type: "error" });
    }
  };

  const inputClass = "w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Rollen</h1>
        {canManage && (
          <button onClick={() => setShowAdd(true)} className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            + Nieuwe rol
          </button>
        )}
      </div>

      {message.text && (
        <div role="status" className={`mb-4 rounded border px-6 py-4 text-sm ${message.type === "success" ? "border-green-200 bg-green-50 text-green-800" : "border-red-200 bg-red-50 text-red-700"}`}>
          {message.text}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter op naam, categorie of beschrijving…"
          aria-label="Filter rollen"
          className="w-96 max-w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {!loading && (
          <span className="text-sm text-gray-500">{filter ? `${visible.length} van ${roles.length}` : roles.length} rollen</span>
        )}
        {sort.key && (
          <button type="button" onClick={() => setSort({ key: null, dir: "asc" })} className="text-sm text-blue-600 hover:underline">
            Toon als hiërarchie
          </button>
        )}
      </div>

      <div className="overflow-x-auto bg-white shadow rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              {COLUMNS.map((column) => {
                const active = sort.key === column.key;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                    onClick={() => toggleSort(column.key)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  >
                    <button type="button" className="uppercase tracking-wider">
                      {column.label} {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading &&
              [0, 1, 2].map((i) => (
                <tr key={i} aria-hidden="true"><td colSpan={COLUMNS.length} className="px-6 py-4"><div className="h-4 w-full animate-pulse rounded bg-gray-100" /></td></tr>
              ))}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="px-6 py-10 text-center text-gray-400">Geen rollen gevonden</td></tr>
            )}
            {visible.map((role) => (
              <tr key={role.id} onClick={() => navigate(`/roles/${role.id}`)} className="cursor-pointer hover:bg-gray-50">
                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                  <span className={role.depth ? "ml-5 inline-flex items-center gap-1" : "inline-flex items-center gap-2"}>
                    {role.depth ? <span aria-hidden="true" className="text-gray-300">↳</span> : null}
                    <span data-name>{role.name}</span>
                    {role.is_primary && (
                      <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">basis</span>
                    )}
                    {!sort.key ? null : role.parent_name && <span className="text-xs font-normal text-gray-400">onder {role.parent_name}</span>}
                  </span>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{role.category || <span className="text-gray-300">—</span>}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{role.description || <span className="text-gray-300">—</span>}</td>
                <td className="px-6 py-4 text-sm tabular-nums text-gray-700">{role.user_count || 0}</td>
                <td className="px-6 py-4 text-sm tabular-nums text-gray-700">{role.permission_count ?? 0}</td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{new Date(role.created_at).toLocaleDateString("nl-BE")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" role="dialog" aria-label="Nieuwe rol">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">Nieuwe rol</h3>
            <form onSubmit={create}>
              <div className="mb-4">
                <label htmlFor="new-name" className="mb-1 block text-sm font-medium text-gray-700">Naam *</label>
                <input id="new-name" required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
              </div>
              <div className="mb-4">
                <label htmlFor="new-category" className="mb-1 block text-sm font-medium text-gray-700">Categorie</label>
                <input id="new-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="bv. Voorraad, Verkoop, Timesheets" className={inputClass} />
              </div>
              <div className="mb-6">
                <label htmlFor="new-description" className="mb-1 block text-sm font-medium text-gray-700">Beschrijving</label>
                <textarea id="new-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked, parent_role_id: "" })} />
                  Primaire rol (basis voor een menusectie)
                </label>
              </div>
              {!form.is_primary && (
                <div className="mb-4">
                  <label htmlFor="new-parent" className="mb-1 block text-sm font-medium text-gray-700">Vereist primaire rol</label>
                  <select id="new-parent" value={form.parent_role_id} onChange={(e) => setForm({ ...form, parent_role_id: e.target.value })} className={inputClass}>
                    <option value="">Geen (zelfstandige rol)</option>
                    {roles.filter((r) => r.is_primary).map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <p className="mb-4 text-xs text-gray-500">Na het aanmaken kies je op de rolpagina welke rechten de rol geeft.</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Annuleren</button>
                <button type="submit" className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">Aanmaken</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
